/*
 * MigrationExport.ts (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all it's contents are proprietary and confidential.
 *
 * @file MigrationExport.ts
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

import { handleError } from '@abstract/abstractwebcommon-client/ErrorHandler/ErrorHandler';
import { asyncErrorHandler } from '@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler';
import { IAPIEntityResponse } from '@abstract/abstractwebcommon-shared/interfaces/api';
import {
  IMigrationExportSummary,
  IMigrationExportToken
} from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/MigrationExport';
import {
  getMigrationExportSummaryService,
  createMigrationExportTokenService,
  migrationExportDownloadURL,
  migrationDocumentsDownloadURL
} from '../Services/MigrationExport';

const GET_MIGRATION_SUMMARY_REQUEST = 'migrationExport/summary/request';
const GET_MIGRATION_SUMMARY_SUCCESS = 'migrationExport/summary/success';
const GET_MIGRATION_SUMMARY_FAILURE = 'migrationExport/summary/failure';

const START_MIGRATION_DOWNLOAD_REQUEST = 'migrationExport/download/request';
const START_MIGRATION_DOWNLOAD_SUCCESS = 'migrationExport/download/success';
const START_MIGRATION_DOWNLOAD_FAILURE = 'migrationExport/download/failure';

const INITIAL_STATE = {
  summary: null as IMigrationExportSummary | null,
  isFetchingSummary: false,
  isPreparingDownload: false
};

export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case GET_MIGRATION_SUMMARY_REQUEST:
      return { ...state, isFetchingSummary: true };
    case GET_MIGRATION_SUMMARY_SUCCESS:
      return {
        ...state,
        isFetchingSummary: false,
        summary: action.payload.summary
      };
    case GET_MIGRATION_SUMMARY_FAILURE:
      return { ...state, isFetchingSummary: false };
    case START_MIGRATION_DOWNLOAD_REQUEST:
      return { ...state, isPreparingDownload: true };
    case START_MIGRATION_DOWNLOAD_SUCCESS:
      return { ...state, isPreparingDownload: false };
    case START_MIGRATION_DOWNLOAD_FAILURE:
      return { ...state, isPreparingDownload: false };
    default:
      return state;
  }
};

const getMigrationSummaryRequest = () => ({
  type: GET_MIGRATION_SUMMARY_REQUEST
});

export const getMigrationSummarySuccess = (
  summary: IMigrationExportSummary
) => ({
  type: GET_MIGRATION_SUMMARY_SUCCESS,
  payload: { summary }
});

const getMigrationSummaryFailure = (error: string) => ({
  type: GET_MIGRATION_SUMMARY_FAILURE,
  error
});

const startMigrationDownloadRequest = () => ({
  type: START_MIGRATION_DOWNLOAD_REQUEST
});

const startMigrationDownloadSuccess = () => ({
  type: START_MIGRATION_DOWNLOAD_SUCCESS
});

const startMigrationDownloadFailure = (error: string) => ({
  type: START_MIGRATION_DOWNLOAD_FAILURE,
  error
});

/**
 * Reads what the export would contain, and the counts that block the cutover.
 */
export const getMigrationExportSummaryAction = () => async (dispatch) => {
  const errorMessage: string = 'Failed to read the migration export summary.';

  try {
    dispatch(getMigrationSummaryRequest());

    const result: IAPIEntityResponse<IMigrationExportSummary> = await asyncErrorHandler(
      getMigrationExportSummaryService()
    );

    if (result.error) {
      handleError({ message: errorMessage });
      dispatch(getMigrationSummaryFailure(result.error as string));
    } else {
      dispatch(
        getMigrationSummarySuccess(
          (result as any).summary as IMigrationExportSummary
        )
      );
    }
  } catch (error) {
    dispatch(getMigrationSummaryFailure(error as string));
    handleError({ message: errorMessage });
  }
};

/**
 * Takes a single-use ticket and hands the browser the download.
 *
 * The navigation is the last thing this does, and nothing is dispatched after it: the ticket is
 * spent on first use, so a retry issued from a stale state would be refused and read to the
 * operator as the export having failed. The screen re-arms by asking for a new ticket.
 *
 * One action for both downloads, taking the URL builder: the two differ only in which endpoint the
 * ticket is spent at, and a second copy of the ticket handling is one that would drift.
 * @param urlOf Builds the download URL from a fresh ticket.
 */
const downloadWithTicket = (urlOf: (token: string) => string) => async (
  dispatch
) => {
  const errorMessage: string =
    'Failed to prepare the migration export download.';

  try {
    dispatch(startMigrationDownloadRequest());

    const result: IAPIEntityResponse<IMigrationExportToken> = await asyncErrorHandler(
      createMigrationExportTokenService()
    );

    const token: string = (result as any)?.token;

    if (result.error || !token) {
      handleError({ message: errorMessage });
      dispatch(
        startMigrationDownloadFailure((result.error as string) ?? errorMessage)
      );
      return;
    }

    dispatch(startMigrationDownloadSuccess());
    window.location.assign(urlOf(token));
  } catch (error) {
    dispatch(startMigrationDownloadFailure(error as string));
    handleError({ message: errorMessage });
  }
};

/** Downloads the records — catalogue, customers, subscriptions, transactions. */
export const downloadMigrationExportAction = () =>
  downloadWithTicket(migrationExportDownloadURL);

/** Downloads the legacy invoice PDFs, which travel separately. */
export const downloadMigrationDocumentsAction = () =>
  downloadWithTicket(migrationDocumentsDownloadURL);
